import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listSites, listSubjectGrants, getResolvedSites, setSubjectSites, type SiteSummary,
} from "../api/rest";
import { IdpUserPicker } from "./IdpUserPicker";
import css from "./AccessAdminPage.module.css";

const ROLES = ["viewer", "manager", "admin"] as const;

export function AccessAdminPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [subjectInput, setSubjectInput] = useState("");
  const [subject, setSubject] = useState("");          // committed (loaded) subject
  const [role, setRole] = useState<string>("viewer");
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [saved, setSaved] = useState(false);

  const { data: sites = [] } = useQuery<SiteSummary[]>({ queryKey: ["allSites"], queryFn: listSites });

  const grantsQ = useQuery({
    queryKey: ["subjectGrants", subject],
    queryFn: () => listSubjectGrants(subject),
    enabled: !!subject,
  });
  const resolvedQ = useQuery({
    queryKey: ["resolvedSites", subject],
    queryFn: () => getResolvedSites(subject),
    enabled: !!subject,
  });

  // Seed the checkboxes + role from the loaded subject's existing site grants.
  // Guarded adjust-during-render (react.dev pattern): re-seeds once per fetched
  // grants identity (load or post-save refetch), same as the old effect did.
  const [seededFrom, setSeededFrom] = useState<unknown>(null);
  if (grantsQ.data && grantsQ.data !== seededFrom) {
    setSeededFrom(grantsQ.data);
    const siteGrants = grantsQ.data.filter((g) => g.scopeType === "site");
    setChecked(new Set(siteGrants.map((g) => g.scopeId)));
    if (siteGrants[0]) setRole(siteGrants[0].role);
    setSaved(false);
  }

  const save = useMutation({
    mutationFn: () => setSubjectSites(subject, [...checked], role),
    onSuccess: () => {
      setSaved(true);
      qc.invalidateQueries({ queryKey: ["subjectGrants", subject] });
      qc.invalidateQueries({ queryKey: ["resolvedSites", subject] });
    },
  });

  const load = () => {
    const s = subjectInput.trim();
    if (s) { setSubject(s); setSaved(false); }
  };
  const toggle = (id: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    setSaved(false);
  };

  const orgGrants = (grantsQ.data ?? []).filter((g) => g.scopeType === "org_unit");
  const resolvedIds = resolvedQ.data?.siteIds ?? [];
  const nameOf = (id: string) => sites.find((s) => s.id === id)?.name ?? id;

  return (
    <div className={css.root}>
      <div className={css.header}>
        <div className={css.title}>{t("accessAdmin.title")}</div>
        <div className={css.subtitle}>{t("accessAdmin.subtitle")}</div>
      </div>

      <form className={css.loader} onSubmit={(e) => { e.preventDefault(); load(); }}>
        <div className={`${css.field} ${css.grow}`}>
          <label className={css.label} htmlFor="subj">{t("accessAdmin.subjectLabel")}</label>
          <IdpUserPicker id="subj" value={subjectInput} onChange={setSubjectInput}
                         inputClassName={`${css.input} ${css.mono}`}
                         placeholder={t("accessAdmin.subjectPlaceholder")} />
        </div>
        <button type="submit" className={css.btnGhost} disabled={!subjectInput.trim()}>
          {t("accessAdmin.load")}
        </button>
      </form>

      {subject && (
        <div className={css.card}>
          <div className={css.cardHd}>
            <span className={css.cardTitle}>{t("accessAdmin.sites")}</span>
            <span className={css.subjectTag}>{subject}</span>
          </div>
          <div className={css.hint}>{t("accessAdmin.hint")}</div>

          <div className={css.field} style={{ marginBottom: "var(--space-4)", maxWidth: 220 }}>
            <label className={css.label} htmlFor="role">{t("accessAdmin.role")}</label>
            <select id="role" className={css.select} value={role} onChange={(e) => { setRole(e.target.value); setSaved(false); }}>
              {ROLES.map((r) => <option key={r} value={r}>{t(`accessAdmin.role_${r}`)}</option>)}
            </select>
          </div>

          {sites.length === 0 ? (
            <div className={css.empty}>{t("accessAdmin.noSites")}</div>
          ) : (
            <div className={css.siteList}>
              {sites.map((s) => {
                const on = checked.has(s.id);
                return (
                  <label key={s.id} className={on ? `${css.siteRow} ${css.on}` : css.siteRow}>
                    <input type="checkbox" checked={on} onChange={() => toggle(s.id)}
                           aria-label={s.name} />
                    <span className={css.siteName}>{s.name}</span>
                    <span className={css.siteId}>{s.id}</span>
                  </label>
                );
              })}
            </div>
          )}

          <div className={css.footer}>
            <button type="button" className={css.btn} disabled={save.isPending || !grantsQ.data}
                    onClick={() => save.mutate()}>
              {save.isPending ? t("common.saving") : t("accessAdmin.save")}
            </button>
            {save.isError ? (
              <span className={css.errorText}>{t("accessAdmin.saveError", { msg: (save.error as Error).message })}</span>
            ) : saved ? (
              <span className={css.savedText}>✓ {t("accessAdmin.saved")}</span>
            ) : null}
          </div>

          {orgGrants.length > 0 && (
            <div className={css.orgNote}>
              {t("accessAdmin.orgGrants")} <code>{orgGrants.map((g) => g.scopeId).join(", ")}</code>
            </div>
          )}
        </div>
      )}

      {subject && (
        <div className={css.card}>
          <div className={css.cardTitle} style={{ marginBottom: 8 }}>{t("accessAdmin.previewTitle")}</div>
          {resolvedIds.length === 0 ? (
            <div className={css.resolved}>{t("accessAdmin.resolvedNone")}</div>
          ) : (
            <div className={css.resolved}>
              {t("accessAdmin.resolved", { n: resolvedIds.length })}
              <div className={css.chips}>
                {resolvedIds.map((id) => <span key={id} className={css.chip}>{nameOf(id)}</span>)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
